import type {
    AvisoObraItem,
    AvisoObraListResponse,
    AvisoObraDetailResponse,
    TipoObraItem,
    TipoResolucionItem,
    ActividadEspecialItem
} from './avisoObraTypes';

const toAvisoItem = (raw: any): AvisoObraItem => {
    return {
        id: Number(raw.id),
        idOrganismoCentral: Number(raw.idOrganismoCentral),
        idDireccion: Number(raw.idDireccion),
        codigoSrt: raw.codigoSrt ?? null,
        superficieM2: Number(raw.superficieM2 ?? 0),
        cantidadPlantas: Number(raw.cantidadPlantas ?? 0),
        descripcionObra: String(raw.descripcionObra ?? ''),
        fechaInicio: raw.fechaInicio,
        fechaFin: raw.fechaFin,
        fechaAlta: raw.fechaAlta,
        idUsuarioAlta: raw.idUsuarioAlta,
        fechaActualizacion: raw.fechaActualizacion,
        idUsuarioActualizacion: raw.idUsuarioActualizacion,
        fechaBaja: raw.fechaBaja,
        idUsuarioBaja: raw.idUsuarioBaja,
        motivoBaja: raw.motivoBaja,
        idTipoResolucion: Number(raw.idTipoResolucion),
        idEstadosAvisoObra: Number(raw.idEstadosAvisoObra),
        idEstablecimiento: raw.idEstablecimiento ?? null,
        programaSeguridadPresentado: Boolean(raw.programaSeguridadPresentado),

        direccion: {
            id: Number(raw.direccion?.id),
            calle: raw.direccion?.calle ?? '',
            altura: raw.direccion?.altura ?? '',
            piso: raw.direccion?.piso ?? null,
            departamento: raw.direccion?.departamento ?? null,
            latitud: raw.direccion?.latitud ?? '',
            longitud: raw.direccion?.longitud ?? '',
            localidad: {
                id: Number(raw.direccion?.localidad?.id),
                nombre: raw.direccion?.localidad?.nombre ?? '',
                codigoPostal: Number(raw.direccion?.localidad?.codigoPostal),
                provincia: {
                    id: Number(raw.direccion?.localidad?.provincia?.id),
                    nombre: raw.direccion?.localidad?.provincia?.nombre ?? ''
                }
            }
        },

        responsables: Array.isArray(raw.responsables)
            ? raw.responsables.map((r: any) => ({
                  idResponsable: Number(r.idResponsable),
                  nombre: r.nombre ?? '',
                  apellido: r.apellido ?? '',
                  cuitCuilCuip: r.cuitCuilCuip ?? null,
                  tituloHabilitante: r.tituloHabilitante ?? null,
                  nroMatricula: r.nroMatricula ?? null,
                  entidadOtorgante: r.entidadOtorgante ?? null
              }))
            : [],

        extensionesObra: raw.extensionesObra ?? [],
        suspensionesObra: raw.suspensionesObra ?? [],

        avisosObraActividadesEspeciales: Array.isArray(raw.avisosObraActividadesEspeciales)
            ? raw.avisosObraActividadesEspeciales.map((a: any) => ({
                  idAvisoObraActividad: a.idAvisoObraActividad,
                  idAvisoObra: a.idAvisoObra,
                  idActividad: a.idActividad,
                  codigoActividad: a.codigoActividad,
                  descripcionActividad: a.descripcionActividad,
                  idResolucion: a.idResolucion,
                  fechaInicio: a.fechaInicio,
                  fechaFin: a.fechaFin
              }))
            : [],

        avisosObraTiposSubtipos: Array.isArray(raw.avisosObraTiposSubtipos)
            ? raw.avisosObraTiposSubtipos.map((t: any) => ({
                  idTipoObra: t.idTipoObra,
                  idSubtiposObra: Array.isArray(t.idSubtiposObra) ? t.idSubtiposObra : []
              }))
            : []
    };
};

export const transformAvisosResponse = (response: AvisoObraListResponse) => {
    return response.data;
};

export const transformAvisoDetailResponse = (resp: AvisoObraDetailResponse): AvisoObraItem => {
    const raw = resp?.data ?? {};
    return toAvisoItem(raw);
};

export const transformTiposObraResponse = (resp: any): TipoObraItem[] => {
    const list = Array.isArray(resp?.data) ? resp.data : [];

    return list.map((x: any) => ({
        id: Number(x?.id ?? 0),
        nombre: String(x?.nombre ?? ''),
        subtipos: Array.isArray(x?.subtipos)
            ? x.subtipos.map((sub: any) => ({
                  id: Number(sub?.id ?? 0),
                  codigo: Number(sub?.codigo ?? 0),
                  descripcion: String(sub?.descripcion ?? ''),
                  idTipoAvisoObra: Number(sub?.idTipoAvisoObra ?? x.id ?? 0)
              }))
            : []
    }));
};

export const transformTiposResolucionResponse = (response: any): TipoResolucionItem[] => {
    const items = Array.isArray(response?.data?.tiposResolucion)
        ? response.data.tiposResolucion
        : [];

    return items
        .map(
            (raw: any): TipoResolucionItem => ({
                id: Number(raw?.id),
                name: String(raw?.descripcion ?? ''),
                requierePrograma: Boolean(raw?.requierePrograma),
                descripcion: ''
            })
        )
        .filter((it: TipoResolucionItem) => Number.isFinite(it.id) && it.name.trim() !== '');
};

export const transformActividadesEspecialesResponse = (response: any): ActividadEspecialItem[] => {
    const items = Array.isArray(response?.data?.actividades) ? response.data.actividades : [];

    return items
        .map(
            (raw: any): ActividadEspecialItem => ({
                id: Number(raw?.id ?? 0),
                codigo: Number(raw?.codigo ?? 0),
                descripcion: String(raw?.descripcion ?? ''),
                actividad: Boolean(raw?.actividad),
                idAvisoObraActividad: 0,
                idAvisoObra: 0,
                idActividad: 0,
                codigoActividad: 0,
                descripcionActividad: '',
                idResolucion: null,
                fechaInicio: null,
                fechaFin: null
            })
        )
        .filter((it: any) => Number.isFinite(it.id) && it.descripcion.trim() !== '');
};
